import { enumerateDevices, getUserMedia } from "./media-devices"

/** @returns `true` if user has granted camera permissions, `false` otherwise */
export const requestCameraPermission = async (): Promise<boolean> => {
  const isAlreadyGranted = (await getCameraPermissionStatus())?.state === "granted"

  /**
   * In Safari, when LIQA is integrated into an iframe, the `navigator.permissions.query` method may return "granted" if the user has already granted camera permissions to the parent window.
   * However, in the iframe, the camera permissions might not actually be granted, and media devices returned by `enumerateDevices` will be invalid (they will lack `deviceId` and `label`).
   * To ensure that the camera permissions are actually granted, we need to check if the media devices array is valid by verifying the `label` property, which is always empty if the user has not granted camera permissions.
   * Reference: https://developer.mozilla.org/en-US/docs/Web/API/MediaDeviceInfo/label#value
   */
  if (
    isAlreadyGranted &&
    (await enumerateDevices()).some((d) => d.kind === "videoinput" && d.label.length > 0)
  )
    return true

  if (typeof navigator.mediaDevices === "undefined") return false

  return await getUserMedia({ video: true })
    .then((stream) => stream.getTracks().forEach((track) => track.stop()))
    .then(() => true)
    .catch(() => false)
}

/** Returns current camera permission status. Returns null when legacy permissions API is detected. */
export const getCameraPermissionStatus = async (): Promise<PermissionStatus | null> => {
  if (await checkLegacyPermissionsAPI()) return null

  return navigator.permissions.query({ name: "camera" as PermissionName }).catch(() => null)
}

// As of June 2023, the Permissions API is not supported by Firefox and Safari < 16
export const checkLegacyPermissionsAPI = async (): Promise<boolean> => {
  if (!("permissions" in navigator)) return true

  return navigator.permissions
    .query({ name: "camera" as PermissionName })
    .then(() => false)
    .catch(() => true)
}
